const { summarizeLegacyBgpContext, parseLegacyVantagePointName } = require('@kentik/ui-shared/synthetics/bgpLegacy');

const V1_BGP_INVALID_ORIGIN = 'bgp-monitor-invalid-origin';
const V1_BGP_REACHABILITY = 'bgp-monitor-visibility';
const BGP_INVALID_ORIGIN = 'bgp-invalid-origin';
const BGP_REACHABILITY = 'bgp-reachability';

const rpkiConversionMap = {
  0: 'Unknown',
  1: 'Not Found',
  2: 'Invalid (explicit)',
  3: 'Invalid (prefix)',
  4: 'Invalid',
  5: 'Valid',
  8: 'Validation Error'
};

const TFZ_FILTERS = [174, 701, 1239, 1299, 2914, 3257, 3320, 3356, 3491, 5511, 6453, 6461, 6762, 7018, 7922, 12956];

const invalidConversionMap = {
  rpki: {
    type: 'invalidRpki',
    type_display: ({ rpki_status }) => `RPKI ${rpkiConversionMap[rpki_status] || 'Invalid'}`
  },
  origin: {
    type: 'unexpectedOrigin',
    type_display: () => 'Unexpected Origin'
  },
  upstream: {
    type: 'unexpectedUpstream',
    type_display: () => 'Unexpected Upstream'
  }
};

const validateOrigin = (config, row) => {
  const { origin = [], check_rpki = true, routeviewer = false } = config;
  const { origin_asn, rpki_status } = row;
  const origin_asn_string = origin_asn?.toString() || '';

  if (!!routeviewer || (!origin.length && !check_rpki)) {
    return {
      invalid: false,
      type: 'escape'
    };
  }

  // logic mirrors filters defined for invalidOrigin policy: src/node/services/synthetics/bgpPolicy.js
  // note: there exists rpki status that are both not invalid and not valid
  const checkBothOriginAndRPKI = !!origin.length && check_rpki;
  const checkOnlyRPKI = !origin.length && check_rpki;
  const checkOnlyOrigin = !!origin.length && !check_rpki;

  // invalid if rpki_status is not valid and origin is not defined in config
  if (checkBothOriginAndRPKI) {
    return {
      invalid: rpki_status !== 5 && !origin.includes(origin_asn_string),
      type: 'rpki' // invalid rpki supersedes invalid origin
    };
  }

  // invalid only if rpki_status is invalid
  if (checkOnlyRPKI) {
    return {
      invalid: rpki_status === 2 || rpki_status === 3 || rpki_status === 4,
      type: 'rpki'
    };
  }

  // invalid only if origin is not defined in config, and not checking rpki
  if (checkOnlyOrigin) {
    return {
      invalid: !origin.includes(origin_asn_string),
      type: 'origin'
    };
  }

  return {
    invalid: false,
    type: 'escape'
  };
};

const validateUpstream = (config, row) => {
  const { upstream = [], routeviewer = false } = config;
  const { upstream_asn } = row;
  const upstream_asn_string = upstream_asn?.toString() || '';

  if (!!routeviewer || !upstream.length) {
    return {
      invalid: false,
      type: 'escape'
    };
  }

  // TFZ membership is currently inert (TFZ_FILTERS are numbers, upstream_asn_string
  // is a string); preserved as-is. Path-aware labeling lives in detectUpstreamLeak.
  return {
    invalid: ![].concat(TFZ_FILTERS, upstream).includes(upstream_asn_string),
    type: 'upstream'
  };
};

// Collapse AS-path prepending (consecutive duplicate ASNs) into a single hop.
const stripPrepending = (asPath) => asPath.filter((asn, i) => i === 0 || asn !== asPath[i - 1]);

/**
 * Upstream-leak detection for a single route's AS path.
 *
 * Mirrors the alarm-side logic: a prefix is "leaking" when it reaches the
 * transit-free zone (Tier-1 core) via an upstream the customer did not
 * authorize. The AS path is ordered with the origin as the LAST element.
 *
 * @param {object} config - BGP test config ({ upstream: AllowedUpstream, routeviewer })
 * @param {object} params
 * @param {string[]} params.asPath - AS path, origin last
 * @returns {{ leaking: boolean, upstreamASNs: string[] }} responsible hop-1 ASNs
 */
const detectUpstreamLeak = (config, { asPath = [] }) => {
  const { upstream = [], routeviewer = false } = config;

  if (!!routeviewer || !upstream.length) {
    return { leaking: false, upstreamASNs: [] };
  }

  const path = stripPrepending((asPath || []).filter(Boolean).map(String));

  if (path.length < 2) {
    return { leaking: false, upstreamASNs: [] };
  }

  // Hop-1: the first AS after the origin.
  const hop1 = path[path.length - 2];

  if (upstream.map(String).includes(hop1)) {
    return { leaking: false, upstreamASNs: [] };
  }

  // Everything except the origin; flag the hop-1 neighbour if the path reaches the TFZ.
  const tfz = new Set(TFZ_FILTERS.map(String));
  const restOfPath = path.slice(0, path.length - 1);
  const reachesTfz = restOfPath.some((asn) => tfz.has(asn));

  return reachesTfz ? { leaking: true, upstreamASNs: [hop1] } : { leaking: false, upstreamASNs: [] };
};

const getPolicyType = (name) => {
  if (name.endsWith(BGP_INVALID_ORIGIN) || name.endsWith(V1_BGP_INVALID_ORIGIN)) {
    return 'origin';
  }

  if (name.endsWith(BGP_REACHABILITY) || name.endsWith(V1_BGP_REACHABILITY)) {
    return 'reachability';
  }

  return 'unknown';
};

const CONDITION_TYPE_LABELS = {
  CONDITION_TYPE_UNSPECIFIED: 'Unspecified',
  CONDITION_TYPE_UNREACHABLE: 'Reachability',
  CONDITION_TYPE_ORIGIN: 'Origin Hijack',
  CONDITION_TYPE_LEAKING: 'Upstream Leak'
};

const percentage = (fraction) => {
  if (!Number.isFinite(fraction)) {
    return null;
  }

  return `${Number((fraction * 100).toFixed(1))}%`;
};

/*
  Role phrasing mirrors leakFlaggedASNs/originFlaggedASNs in ktrac's evm.go so the drawer and the
  notification for one alarm describe an ASN the same way. A culprit only means something next to
  its reason: a misplaced upstream is on the customer's own allow-list and is flagged for where it
  sits, and a transit-core member is evidence the route escaped rather than the ASN that leaked it.
  The qualifier names a second ASN only where the reason distinguishes the two, which
  unlisted_upstream does not -- it sets both to the same hop.

  Label and qualifier are carried apart so a renderer can tag the reason and place the qualifier
  beside the ASN; flaggedAsnRoleText rejoins them into the phrase ktrac sends.
*/
const flaggedAsnRoleParts = (cause) => {
  switch (cause?.reason) {
    case 'misplaced_upstream':
      return { roleLabel: 'misplaced upstream', roleQualifier: `above AS${cause.upstream_asn}` };
    case 'transit_core_reached':
      return { roleLabel: 'transit core member', roleQualifier: `via AS${cause.upstream_asn}` };
    case 'unlisted_upstream':
      return { roleLabel: 'unlisted upstream', roleQualifier: null };
    case 'unlisted_origin':
      return { roleLabel: 'unlisted origin', roleQualifier: null };
    case 'rpki_invalid':
      return { roleLabel: 'invalid RPKI', roleQualifier: null };
    default:
      return {
        roleLabel: typeof cause?.reason === 'string' ? cause.reason.replace(/_/g, ' ') : null,
        roleQualifier: null
      };
  }
};

// flaggedAsnRoleText renders a row's role as one phrase, for plain-text contexts like a tooltip.
const flaggedAsnRoleText = ({ roleLabel, roleQualifier } = {}) => [roleLabel, roleQualifier].filter(Boolean).join(', ');

/*
  One row per cause rather than per ASN: causes group by (reason, actors), so an ASN that is a
  misplaced upstream on some prefixes and a transit-core member on others earns a row for each.
  Sorted by VP count descending then ASN, so the ASN most vantage points named reads first and a
  smear across many ASNs is visible as a flat tail.

  Per-ASN RPKI status is carried only when the alarm holds more than one distinct status, since a
  uniform alarm is already stated once by the RPKI Status row. This matches rpkiSummaryDetail in
  ktrac's evm.go, which renders the alarm-level detail on the same condition.
*/
const flaggedAsnRows = (causes, actorField, attributeRpki) =>
  causes
    .map((cause) => ({
      asn: cause?.[actorField],
      ...flaggedAsnRoleParts(cause),
      vpCount: cause?.vp_count,
      ...(attributeRpki && cause?.rpki_status ? { rpkiStatus: cause.rpki_status } : {})
    }))
    .filter((row) => Number.isFinite(row.asn))
    .sort((left, right) => (right.vpCount ?? 0) - (left.vpCount ?? 0) || left.asn - right.asn);

/*
  One row per prefix finding, the same rows ktrac renders as routing_issues prose. Reason phrasing
  is shared with flaggedAsnRows because prefix findings and causes carry the same reason enums and
  the same actor fields. Sorted by VP count descending then prefix, so the finding the most vantage
  points observed reads first.
*/
const prefixFindingRows = (findings, actorField, attributeRpki) =>
  findings
    .map((finding) => ({
      prefix: finding?.prefix,
      asn: finding?.[actorField],
      ...flaggedAsnRoleParts(finding),
      vpCount: finding?.vp_count,
      ...(attributeRpki && finding?.rpki_status ? { rpkiStatus: finding.rpki_status } : {})
    }))
    .filter((row) => row.prefix && Number.isFinite(row.asn))
    .sort(
      (left, right) => (right.vpCount ?? 0) - (left.vpCount ?? 0) || String(left.prefix).localeCompare(right.prefix)
    );

/*
  Reads the compiled evidence off a trigger summary into the shape the alarm drawer renders. The
  evidence is exact and self-describing, so nothing here re-derives a verdict from AS paths; each
  count and list is carried, and a list shorter than its companion count is one ktrac capped.
*/
const summarizeBgpEvidence = (summary, subtest) => {
  const evidence = summary?.evidence;

  if (!evidence) {
    return {};
  }

  const affectedVPCount = evidence.affected_vp_count ?? 0;
  const affectedVps = Array.isArray(evidence.affected_vp_keys) ? evidence.affected_vp_keys : [];
  const affectedPrefixes = Array.isArray(evidence.affected_pfxs) ? evidence.affected_pfxs : [];
  const totalVps = summary.total_vps ?? 0;
  const affectedPercentage = totalVps > 0 ? percentage(affectedVPCount / totalVps) : null;
  const result = {
    summary: summary.summary,
    // affected and total read as one fact, since either alone leaves the reader doing the division
    vantagePoints: affectedPercentage
      ? `${affectedVPCount} of ${totalVps} (${affectedPercentage})`
      : `${affectedVPCount}`,
    // the magnitude behind the formatted vantagePoints, for anything that ranks alarms against each other
    affectedVpCount: affectedVPCount,
    totalVps,
    affectedPrefixes,
    affectedPrefixesTruncated: affectedPrefixes.length < (evidence.affected_pfx_count ?? 0)
  };

  if (subtest === 'CONDITION_TYPE_UNREACHABLE') {
    result.unreachableVps = affectedVps;
    result.unreachableVpsTruncated = affectedVps.length < affectedVPCount;

    const reachablePercentage = percentage(summary.reachable_vps_fraction);
    if (reachablePercentage) {
      const reachableVps = Math.max(0, totalVps - affectedVPCount);
      result.currentReachability = `${reachablePercentage} (${reachableVps} ${reachableVps === 1 ? 'VP' : 'VPs'})`;
    }
  } else {
    result.affectedVps = affectedVps;
    result.affectedVpsTruncated = affectedVps.length < affectedVPCount;
  }

  const causes = evidence.leak?.causes || evidence.origin?.causes || [];
  const causeCount = evidence.leak?.cause_count ?? evidence.origin?.cause_count ?? 0;
  const actorField = evidence.leak ? 'culprit_asn' : 'origin_asn';
  const rpkiStatuses = evidence.origin?.rpki_statuses;
  const attributeRpki = (rpkiStatuses?.length ?? 0) > 1;
  result.flaggedAsns = flaggedAsnRows(causes, actorField, attributeRpki);
  result.flaggedAsnsTruncated = causes.length < causeCount;

  const prefixFindings = evidence.leak?.prefix_findings || evidence.origin?.prefix_findings || [];
  const prefixFindingCount = evidence.leak?.prefix_finding_count ?? evidence.origin?.prefix_finding_count ?? 0;
  result.routingIssues = prefixFindingRows(prefixFindings, actorField, attributeRpki);
  result.routingIssuesTruncated = prefixFindings.length < prefixFindingCount;
  /*
    flaggedAsnRows ranks by vantage point count, so the head is the ASN the most vantage points
    named. Carried as a scalar so the incident log can sort and group alarms by it; a condition
    with no causes, which is every reachability alarm, leaves it null.
  */
  result.topFlaggedAsn = result.flaggedAsns[0]?.asn ?? null;

  if (Array.isArray(rpkiStatuses)) {
    result.rpkiStatuses = rpkiStatuses;
  }

  return result;
};

/*
  Picks the rendering path for one alarm. An alarm compiled by a ktrac that ships
  AlertState.Evidence carries summary.evidence; anything older is readable only through the event
  view model, which synthetics/bgpLegacy.js handles.
*/
const summarizeBgpContext = ({ summary, details, subtest }) =>
  summary?.evidence ? summarizeBgpEvidence(summary, subtest) : summarizeLegacyBgpContext(details);

/*
  Parses a vantage point name into its parts. Names are VPTable.AsKey() strings:
  '<dataset>|<collector>|<peerASN>|<peerIP>|<afi>|<safi>'.
*/
const parseVantagePointAsKey = (name) => {
  const parts = name.split('|');

  if (parts.length !== 6) {
    return null;
  }

  const [dataset, collector, peerAsn, peerIp, afi, safi] = parts;

  if (!dataset || !collector || !peerAsn || !peerIp || !afi || !safi || !/^\d+$/.test(peerAsn)) {
    return null;
  }

  return {
    name,
    dataset,
    collector,
    peerAsn: Number(peerAsn),
    asn: `AS${peerAsn}`,
    ip: peerIp
  };
};

const parseVantagePointName = (name) => {
  if (typeof name !== 'string') {
    return null;
  }

  return parseVantagePointAsKey(name) || parseLegacyVantagePointName(name);
};

/*
  Groups a list of vantage point names by dataset, then by collector, preserving the order in
  which each dataset/collector was first seen

  Names that cannot be parsed are returned separately under 'unparsed' so callers can still show
  them rather than silently dropping data
*/
const groupVantagePointsByCollector = (names) => {
  if (!Array.isArray(names)) {
    return { datasets: [], unparsed: [] };
  }

  const datasets = [];
  const datasetIndex = new Map();
  const unparsed = [];

  names.forEach((name) => {
    const vantagePoint = parseVantagePointName(name);

    if (!vantagePoint) {
      if (typeof name === 'string' && name !== '') {
        unparsed.push(name);
      }

      return;
    }

    if (!datasetIndex.has(vantagePoint.dataset)) {
      const entry = { dataset: vantagePoint.dataset, collectors: [], collectorIndex: new Map() };
      datasetIndex.set(vantagePoint.dataset, entry);
      datasets.push(entry);
    }

    const datasetEntry = datasetIndex.get(vantagePoint.dataset);

    if (!datasetEntry.collectorIndex.has(vantagePoint.collector)) {
      const entry = { collector: vantagePoint.collector, vantagePoints: [] };
      datasetEntry.collectorIndex.set(vantagePoint.collector, entry);
      datasetEntry.collectors.push(entry);
    }

    datasetEntry.collectorIndex.get(vantagePoint.collector).vantagePoints.push(vantagePoint);
  });

  return {
    datasets: datasets.map(({ dataset, collectors }) => ({ dataset, collectors })),
    unparsed
  };
};

module.exports = {
  validateOrigin,
  validateUpstream,
  detectUpstreamLeak,
  getPolicyType,
  rpkiConversionMap,
  invalidConversionMap,
  summarizeBgpEvidence,
  flaggedAsnRoleText,
  summarizeBgpContext,
  parseVantagePointName,
  groupVantagePointsByCollector,
  V1_BGP_INVALID_ORIGIN,
  V1_BGP_REACHABILITY,
  BGP_INVALID_ORIGIN,
  BGP_REACHABILITY,
  TFZ_FILTERS,
  CONDITION_TYPE_LABELS
};
